import { useEffect, useRef, useState } from 'react'
import { useAuthSession } from '../../lib/authSession'
import { Link } from 'react-router-dom'
import { apiFetch, ApiError } from '../../lib/api'
import type { DocumentRef } from '../../lib/documentReader'
import { acceptedSourcesLabel, obligationLabel } from '../../lib/claims'
import DocumentLink from '../documents/DocumentLink'
import { useRead } from '../navigation/useRead'
import { ErrorState } from '../state'
import EvidenceDecisionPane from './EvidenceDecisionPane'
import HouseDateInput from '../HouseDateInput'
import type { ContextRequirement } from '../workbench/contextWork'

interface RequirementDetail {
  id: string
  object_type: string
  object_id: string
  status: string
  obligation_code: string
  obligation: { accepted_sources: string[]; description: string } | null
}

function errorText(error: unknown): string {
  if (error instanceof ApiError) {
    const detail = (error.body as { detail?: unknown } | undefined)?.detail
    if (typeof detail === 'string') return detail
  }
  return error instanceof Error ? error.message : 'The action could not be completed.'
}

/** A real requirement can be acted on even before any task or candidate exists. */
interface EvidenceGapProps {
  requirement: ContextRequirement
  targetLabel: string
  fundId: string | null
  recordHref: string
  chaseHref: string
  documents: readonly DocumentRef[]
  onChanged: () => void
  onPendingChange?: (pending: boolean) => void
}

export default function EvidenceGapPane(props: EvidenceGapProps) {
  const session = useAuthSession()
  return (
    <ScopedEvidenceGapPane
      key={JSON.stringify([
        session,
        props.requirement.id,
        props.requirement.target,
        props.fundId,
        props.recordHref,
        props.chaseHref,
      ])}
      {...props}
    />
  )
}

function ScopedEvidenceGapPane({
  requirement,
  targetLabel,
  fundId,
  recordHref,
  chaseHref,
  documents,
  onChanged,
  onPendingChange,
}: EvidenceGapProps) {
  const [refresh, setRefresh] = useState(0)
  const read = useRead<RequirementDetail>(
    `/api/evidence-status/${encodeURIComponent(requirement.id)}`,
    refresh
  )
  const [selecting, setSelecting] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null)
  const [waiving, setWaiving] = useState(false)
  const [snoozeOpen, setSnoozeOpen] = useState(false)
  const [snoozeMode, setSnoozeMode] = useState<'next_document' | 'timer'>('next_document')
  const [snoozeDate, setSnoozeDate] = useState('')
  const [snoozeResult, setSnoozeResult] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [decisionPending, setDecisionPending] = useState(false)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<'chase' | 'waived' | null>(null)
  const row = read.value
  const kind = row?.object_type === 'asset' ? 'entity' : row?.object_type
  const admitted =
    row?.id === requirement.id &&
    kind === requirement.target.kind &&
    row.object_id === requirement.target.id
  const open =
    admitted && !['satisfied', 'waived', 'not_applicable', 'pending_signoff'].includes(row.status)
  const requested = row?.status === 'requested' || requirement.queue === 'chase'
  const actionsBusy = busy || decisionPending
  useEffect(() => {
    onPendingChange?.(actionsBusy)
    return () => onPendingChange?.(false)
  }, [actionsBusy, onPendingChange])
  const selectedAvailable = documents.some((document) => document.document_id === selectedDocument)
  const changed = () => {
    setRefresh((value) => value + 1)
    onChanged()
  }
  async function act(action: 'chase' | 'waive') {
    if (
      !open ||
      actionsBusy ||
      (action === 'chase' && requested) ||
      (action === 'waive' && !reason.trim())
    )
      return
    setBusy(true)
    setError(null)
    try {
      const receipt = await apiFetch<
        RequirementDetail | { request: { id: string; evidence_status_id: string | null } }
      >(
        `/api/evidence-status/${encodeURIComponent(requirement.id)}/${action === 'chase' ? 'request' : 'waive'}`,
        {
          method: action === 'chase' ? 'POST' : 'PATCH',
          ...(action === 'waive' ? { body: JSON.stringify({ justification: reason.trim() }) } : {}),
        }
      )
      if (!mounted.current) return
      if (action === 'chase') {
        if (
          !('request' in receipt) ||
          receipt.request.evidence_status_id !== requirement.id ||
          !receipt.request.id
        )
          throw new Error(
            'The response did not confirm a request for this requirement. Refresh before retrying.'
          )
      } else if (
        'request' in receipt ||
        receipt.id !== requirement.id ||
        receipt.object_id !== requirement.target.id ||
        (receipt.object_type === 'asset' ? 'entity' : receipt.object_type) !==
          requirement.target.kind ||
        receipt.status !== 'waived'
      ) {
        throw new Error('The response did not confirm this waiver. Refresh before retrying.')
      }
      setSelectedDocument(null)
      setResult(action === 'chase' ? 'chase' : 'waived')
      changed()
    } catch (failure) {
      if (mounted.current) setError(errorText(failure))
    } finally {
      if (mounted.current) setBusy(false)
    }
  }
  async function changeSnooze(wake = false) {
    if (!open || actionsBusy) return
    setBusy(true)
    setError(null)
    try {
      const until =
        snoozeMode === 'timer' && snoozeDate
          ? new Date(`${snoozeDate}T23:59:59`).toISOString()
          : undefined
      const receipt = await apiFetch<{
        evidence_status_id: string
        requirement_status: string
        task_id: string
        task_status: string
        snoozed: boolean
      }>(`/api/evidence-status/${encodeURIComponent(requirement.id)}/${wake ? 'wake' : 'snooze'}`, {
        method: 'POST',
        ...(!wake
          ? { body: JSON.stringify({ mode: snoozeMode, ...(until ? { until } : {}) }) }
          : {}),
      })
      if (!mounted.current) return
      if (
        receipt.evidence_status_id !== requirement.id ||
        !receipt.task_id ||
        receipt.snoozed !== !wake ||
        receipt.task_status !== (wake ? 'open' : 'snoozed')
      )
        throw new Error(
          'The response did not confirm this gap’s snooze state. Refresh before retrying.'
        )
      setSnoozeOpen(false)
      setSnoozeResult(wake ? 'Returned to open work.' : 'Snoozed. The evidence gap remains open.')
      changed()
    } catch (failure) {
      if (mounted.current) setError(errorText(failure))
    } finally {
      if (mounted.current) setBusy(false)
    }
  }
  return (
    <section
      className="context-workbench__section"
      data-testid="evidence-gap"
      data-requirement-id={requirement.id}
    >
      <h3>{requirement.label || obligationLabel(requirement.code)}</h3>
      <p>
        {result === 'waived' ? 'The documented waiver is recorded for ' : 'Evidence for '}
        <Link to={recordHref}>{targetLabel}</Link>.
      </p>
      {read.loading ? <p role="status">Reading the current requirement…</p> : null}
      {read.error ? (
        <ErrorState
          what="evidence requirement"
          error={read.error}
          onRetry={() => setRefresh((value) => value + 1)}
        />
      ) : null}
      {row && !admitted ? (
        <p role="alert">The requirement no longer matches this object. Refresh this context.</p>
      ) : null}
      {admitted && row.obligation?.accepted_sources.length ? (
        <details>
          <summary>Accepted evidence</summary>
          <p>{acceptedSourcesLabel(row.obligation.accepted_sources)}</p>
        </details>
      ) : null}
      {result === 'chase' || (admitted && requested) ? (
        <p role="status">
          {result === 'chase' ? (
            <>
              Added to <Link to={chaseHref}>Chase</Link> for review before sending.
            </>
          ) : (
            <>
              The document request is in <Link to={chaseHref}>Chase</Link>.
            </>
          )}
        </p>
      ) : null}
      {admitted && !open && !result ? (
        <p role="status">This requirement is {row.status.replace(/_/g, ' ')}.</p>
      ) : null}
      {open && !result ? (
        <>
          <div className="context-workbench__task-links">
            <button
              type="button"
              className="vbtn"
              disabled={actionsBusy}
              onClick={() => setSelecting((value) => !value)}
            >
              Select document
            </button>
            <button
              type="button"
              className="vbtn"
              disabled={actionsBusy || requested}
              onClick={() => void act('chase')}
            >
              Chase
            </button>
            <button
              type="button"
              className="vbtn"
              disabled={actionsBusy}
              onClick={() => setWaiving((value) => !value)}
            >
              Waive
            </button>
            <button
              type="button"
              className="vbtn"
              disabled={actionsBusy}
              onClick={() =>
                requirement.snoozed ? void changeSnooze(true) : setSnoozeOpen((value) => !value)
              }
            >
              {requirement.snoozed ? 'Wake' : 'Snooze…'}
            </button>
          </div>
          {snoozeOpen ? (
            <div>
              <label>
                Return to open work{' '}
                <select
                  value={snoozeMode}
                  disabled={actionsBusy}
                  onChange={(event) =>
                    setSnoozeMode(event.target.value as 'next_document' | 'timer')
                  }
                >
                  <option value="next_document">
                    When another document arrives for this object
                  </option>
                  <option value="timer">In 7 days, or on a chosen date</option>
                </select>
              </label>
              {snoozeMode === 'timer' ? (
                <HouseDateInput
                  value={snoozeDate}
                  onChange={setSnoozeDate}
                  label="Snooze until (optional)"
                />
              ) : null}
              <button
                type="button"
                className="vbtn"
                disabled={actionsBusy}
                onClick={() => void changeSnooze()}
              >
                Snooze gap
              </button>
            </div>
          ) : null}
          {selecting ? (
            <div aria-label="Choose a document for this requirement">
              {documents.length ? (
                documents.map((document) => (
                  <div key={document.document_id} className="context-workbench__task-links">
                    <DocumentLink
                      document={document}
                      context={{
                        fundId,
                        focus: `${requirement.target.kind}:${requirement.target.id}`,
                      }}
                    />
                    <button
                      type="button"
                      className="vbtn"
                      disabled={actionsBusy}
                      onClick={() => setSelectedDocument(document.document_id)}
                    >
                      Review for this requirement
                    </button>
                  </div>
                ))
              ) : (
                <p>No documents are linked to this context yet.</p>
              )}
              <Link to="/v2/traces?view=documents">Open Documents or upload a document →</Link>
            </div>
          ) : null}
          {waiving ? (
            <div>
              <label>
                Reason for the missing document
                <textarea value={reason} onChange={(event) => setReason(event.target.value)} />
              </label>
              <button
                type="button"
                className="vbtn"
                disabled={actionsBusy || !reason.trim()}
                onClick={() => void act('waive')}
              >
                Record waiver
              </button>
            </div>
          ) : null}
        </>
      ) : null}
      {snoozeResult || requirement.snoozed ? (
        <p role="status">{snoozeResult ?? 'Snoozed; the evidence gap remains open.'}</p>
      ) : null}
      {error ? <p role="alert">{error}</p> : null}
      {selectedDocument && selectedAvailable && admitted && open && !result ? (
        <EvidenceDecisionPane
          key={`${requirement.id}:${selectedDocument}`}
          requirementId={requirement.id}
          documentId={selectedDocument}
          fundId={fundId}
          target={requirement.target}
          onResolved={changed}
          disabled={busy}
          onPendingChange={setDecisionPending}
        />
      ) : null}
    </section>
  )
}
